import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircleAlert, FileText, Loader2 } from '../../icons.js';
import { ProgressLinear } from '../../atoms/Progress/Progress.js';
import { CollapsibleSection } from '../CollapsibleSection/CollapsibleSection.js';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING } from '../../lib/focus-ring.js';
import { useTranslate } from '../../lib/i18n.js';
/**
 * What the run is doing, and the document it produced.
 *
 * Progress is counted in images, which is the only unit here anyone can
 * measure: a request to a model reports nothing on its way, so a bar creeping
 * through one would be an animation impersonating a measurement. It advances
 * as each page comes back, and the stage line says what is happening in
 * between.
 *
 * ── Why reading has no bar ───────────────────────────────────────────────
 *
 * Preparing does: the browser compresses the images one at a time, so pages
 * finished out of pages chosen is a number that means something. Reading is
 * one request carrying all of them, and it reports nothing until it answers —
 * so a bar creeping across it would be an animation impersonating a
 * measurement. A spinner and a sentence say the true thing.
 *
 * ── Why the content is a `<pre>` ─────────────────────────────────────────
 *
 * The extracted Markdown is generated text from an external service, which
 * makes it untrusted content in exactly the way a tutor's own paste is not.
 * Rendering it as HTML would mean sanitizing it, and sanitizing correctly is
 * a dependency and a permanent obligation. Rendering it as text is safe by
 * construction: there is no element, no attribute and no script, because
 * nothing is parsed at all.
 *
 * That is also the right presentation for what this is for. The tutor is
 * checking a transcription against the paper in their hand, and for that job
 * seeing the literal `## Question 1` matters more than seeing it as a heading.
 * The app renders no Markdown as HTML anywhere, so this adds no exception.
 */
export function ExtractionStatus({ state, showTranscript = true, className, }) {
    const t = useTranslate();
    if (state.status === 'idle')
        return null;
    const panel = 'flex flex-col gap-4 rounded-md border-[1.5px] border-border bg-background p-6';
    if (state.status === 'running') {
        const { stage, imageIndex, imageCount } = state;
        // One request for every page, so there is nothing to count and nothing
        // honest to put a percentage on.
        if (stage === 'reading') {
            return (_jsx("div", { className: cn(panel, className), role: "status", "aria-live": "polite", children: _jsxs("p", { className: "flex items-center gap-3 text-h4 font-semibold text-text", children: [_jsx(Loader2, { "aria-hidden": "true", className: "size-icon-md shrink-0 animate-spin" }), t('ui.extraction.readingLabel')] }) }));
        }
        // Pages compressed, out of pages chosen. A real numerator: the browser
        // does them one at a time.
        return (_jsx("div", { className: cn(panel, className), children: _jsx(ProgressLinear, { value: (imageIndex / Math.max(1, imageCount)) * 100, label: imageIndex === 0
                    ? t('ui.extraction.startingLabel')
                    : t('ui.extraction.imageOf', [imageIndex + 1, imageCount]) }) }));
    }
    if (state.status === 'incomplete') {
        return (_jsxs("div", { className: cn('flex flex-col gap-2 rounded-md border-[1.5px] border-error-border bg-error-light p-6', className), children: [_jsxs("h3", { className: "flex items-center gap-2 text-h4 font-semibold text-error-text", children: [_jsx(CircleAlert, { "aria-hidden": "true", className: "size-icon-md shrink-0" }), t('ui.extraction.failedHeading')] }), _jsx("p", { className: "text-body-sm text-text", children: t('ui.extraction.failedBody') })] }));
    }
    const empty = state.markdown.trim().length === 0;
    // Outside development, a successful read says nothing here: the generated
    // practice is the result a tutor looks at. Only "no text" is theirs to know.
    if (!showTranscript) {
        return empty ? (_jsx("div", { className: cn('rounded-md border-[1.5px] border-accent-border bg-accent-light p-6', className), children: _jsx("p", { className: "text-body-sm text-text", children: t('ui.extraction.noText') }) })) : null;
    }
    return (_jsxs("div", { className: cn('flex flex-col gap-3 rounded-md border-[1.5px] border-accent-border bg-accent-light p-6', className), children: [_jsxs("h3", { className: "flex items-center gap-3 text-h4 font-semibold text-accent-text", children: [_jsx(FileText, { "aria-hidden": "true", className: "size-icon-md shrink-0" }), state.imageCount === 1
                        ? t('count.imagesReadOne')
                        : t('count.imagesReadOther', [state.imageCount])] }), empty ? (_jsx("p", { className: "text-body-sm text-text", children: t('ui.extraction.noText') })) : (_jsx(CollapsibleSection, { title: t('ui.extraction.show'), children: _jsx("pre", { className: cn('max-h-96 overflow-auto rounded-sm border border-border bg-surface px-4 py-3', 'font-mono text-body-sm leading-6 whitespace-pre-wrap text-text', FOCUS_RING), role: "group", tabIndex: 0, "aria-label": t('ui.extraction.contentLabel'), children: state.markdown }) }))] }));
}
//# sourceMappingURL=ExtractionStatus.js.map